import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button } from '@mantine/core';
import { IconClock, IconListNumbers, IconLogin, IconMapPin, IconPlayerPlay, IconRefresh } from '@tabler/icons-react';
import { PROCEDURES_VISIBILITY } from '@actschurch/ccms-sdk';
import { ProcedureAccessError } from '@/components/procedure/AccessScreens';
import { AskSheet } from '@/components/procedure/AskSheet';
import { CalloutList } from '@/components/procedure/ProcedureCallout';
import { PlanningCentreButton } from '@/components/auth/PlanningCentreButton';
import { StepMedia, prefetchImages } from '@/components/media/StepMedia';
import { BackLink, Callout, LoadingState, SectionCard, StickyActionBar } from '@/components/ui';
import { useSafeBack } from '@/components/shell/useSafeBack';
import { errorMessage, isNetworkError } from '@/lib/errors';
import { formatEstimate, formatWhen, plural } from '@/lib/format';
import { buildRunnerModel } from '@/lib/runMerge';
import { ensureMemberRecord, setRunAside, useRunStarter } from '@/lib/runLifecycle';
import { getInProgress, getRunSnapshot, type LocalRunRecord } from '@/lib/runStore';
import { usePortalMode, useProcedureView } from '@/lib/portalApi';
import { usePortalSettings } from '@/lib/settings';
import { useSyncStore } from '@/lib/syncQueue';

/**
 * /p/:code: what the QR code opens. Title, where it is, how long it takes, the safety notes and a
 * cover photo, then Start (or "Carry on where you left off?" when this phone has an unfinished run).
 */
export default function OverviewPage() {
  const { code = '' } = useParams();
  const navigate = useNavigate();
  const back = useSafeBack('/');
  const { mode, userId } = usePortalMode();
  const { settings } = usePortalSettings();
  const query = useProcedureView(code);
  const { startMember } = useRunStarter(code);
  const online = useSyncStore((s) => s.online);
  const pending = useSyncStore((s) => s.entries);
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);
  const [resumeTick, setResumeTick] = useState(0);

  const view = query.data?.view;

  // The unfinished run to offer: this phone's own, or the signed-in person's from the API.
  const resume: LocalRunRecord | null = useMemo(() => {
    void resumeTick;
    const local = getInProgress(code, userId);
    if (local) return local;
    if (view?.resumeRunID && mode === 'member') {
      const record = ensureMemberRecord({ runId: view.resumeRunID, code, title: view.title, userId });
      // Set aside on this phone already (the abandon may still be waiting to sync).
      return record.state === 'active' ? record : null;
    }
    return null;
  }, [code, userId, view?.resumeRunID, view?.title, mode, resumeTick]);

  const resumeProgress = useMemo(() => {
    if (!resume || !view) return null;
    const model = buildRunnerModel(view, getRunSnapshot(resume.runId), pending.filter((e) => e.runId === resume.runId));
    return model;
  }, [resume, view, pending]);

  // Warm the photo cache so every step opens without signal later.
  useEffect(() => {
    if (!view || query.data?.offline) return;
    const urls: (string | null | undefined)[] = [view.cover?.url, ...view.media.map((m) => m.url)];
    view.steps.forEach((s) => s.media.forEach((m) => urls.push(m.url, m.thumbUrl)));
    prefetchImages(urls);
  }, [view, query.data?.offline]);

  if (!view && !query.isError) {
    return (
      <div className="proc-main">
        <LoadingState label="Opening the procedure" count={2} />
      </div>
    );
  }
  if (!view) {
    return <ProcedureAccessError code={code} error={query.error} onRetry={() => void query.refetch()} retrying={query.isFetching} />;
  }

  const sortedSteps = [...view.steps].sort((a, b) => a.sortOrder - b.sortOrder);
  const publicBlocked = mode === 'public' && !settings.publicRunsEnabled;
  const isPrivate = view.visibilityID === PROCEDURES_VISIBILITY.Private;

  const openRun = (runId: number) => navigate(`/p/${encodeURIComponent(code)}/run/${runId}`);

  const start = async () => {
    setStartError(null);
    if (mode === 'public') {
      navigate(`/p/${encodeURIComponent(code)}/start`);
      return;
    }
    if (!online) {
      setStartError('You need signal to start. Move to where you have signal, tap Start, then you can carry on without it.');
      return;
    }
    setStarting(true);
    try {
      const runId = await startMember(view.title, userId);
      openRun(runId);
    } catch (err) {
      setStartError(
        isNetworkError(err)
          ? 'You need signal to start. Move to where you have signal, tap Start, then you can carry on without it.'
          : errorMessage(err),
      );
    } finally {
      setStarting(false);
    }
  };

  const startAgain = () => {
    if (resume) setRunAside(resume);
    setResumeTick((t) => t + 1);
    void start();
  };

  return (
    <div className="proc-main proc-hasbar">
      <div className="proc-pagebody">
      <BackLink label="All procedures" onClick={back} />
      <header className="proc-overview-head">
        {view.categoryPath && <p className="proc-eyebrow">{view.categoryPath}</p>}
        <h1 className="proc-title">{view.title}</h1>
        <div className="proc-facts">
          {view.locationName && <span><IconMapPin size={18} aria-hidden="true" /> {view.locationName}</span>}
          {view.estimatedMinutes ? <span><IconClock size={18} aria-hidden="true" /> {formatEstimate(view.estimatedMinutes)}</span> : null}
          <span><IconListNumbers size={18} aria-hidden="true" /> {plural(sortedSteps.length, 'step')}</span>
          {isPrivate && <span>Team only</span>}
        </div>
        {view.summary && <p className="proc-lede">{view.summary}</p>}
      </header>

      {query.data?.offline && (
        <Callout tone="info" title="Saved copy">
          No signal, so this is the copy saved on this phone {query.data.savedAt ? formatWhen(new Date(query.data.savedAt)).toLowerCase() : ''}.
        </Callout>
      )}

      {view.cover && <StepMedia media={[view.cover]} label={view.title} compact onExpired={() => void query.refetch()} />}

      {view.callouts.length > 0 && (
        <section aria-labelledby="before-you-start" className="proc-section">
          <h2 id="before-you-start" className="proc-sectlabel">Before you start</h2>
          <CalloutList callouts={view.callouts} />
        </section>
      )}

      {view.media.length > 0 && (
        <section aria-label="Photos and files" className="proc-section">
          <StepMedia media={view.media.filter((m) => m.mediaID !== view.cover?.mediaID)} label={view.title} onExpired={() => void query.refetch()} />
        </section>
      )}

      {resume && (
        <SectionCard title="Carry on where you left off?" description={`Started ${formatWhen(new Date(resume.startedAtMs))}${resume.runnerName ? ` by ${resume.runnerName}` : ''}.`}>
          {resumeProgress && (
            <p className="proc-muted">
              {resumeProgress.answered} of {resumeProgress.total} steps ticked off.
            </p>
          )}
          <div className="proc-btnstack">
            <Button size="lg" fullWidth leftSection={<IconPlayerPlay size={20} aria-hidden="true" />} onClick={() => openRun(resume.runId)}>
              Carry on
            </Button>
            <Button variant="default" fullWidth leftSection={<IconRefresh size={18} aria-hidden="true" />} onClick={startAgain} loading={starting} disabled={publicBlocked}>
              Start again
            </Button>
          </div>
        </SectionCard>
      )}

      <section aria-labelledby="steps-outline" className="proc-section">
        <h2 id="steps-outline" className="proc-sectlabel">What you'll do</h2>
        <ol className="proc-outline">
          {sortedSteps.map((s, i) => (
            <li key={s.stepID}>
              <span className="n" aria-hidden="true">{i + 1}</span>
              <span className="s">
                {s.title}
                {s.isOptional && <span className="opt"> (optional)</span>}
              </span>
            </li>
          ))}
        </ol>
      </section>

      {publicBlocked && (
        <SectionCard title="Sign in to run this procedure">
          <p className="proc-muted">Running procedures without signing in is switched off at the moment.</p>
          <div className="proc-btnstack">
            <PlanningCentreButton redirectTo={`/p/${encodeURIComponent(code)}`} primary />
            <Button component={Link} to={`/auth/login?redirect=${encodeURIComponent(`/p/${code}`)}`} variant="subtle" leftSection={<IconLogin size={18} aria-hidden="true" />} fullWidth>
              Sign in with email
            </Button>
          </div>
        </SectionCard>
      )}

      {view.aiChatAvailable && (
        <AskSheet
          code={code}
          title={view.title}
          mode={mode}
          runId={resume && resume.mode === mode ? resume.runId : null}
          runToken={resume && resume.mode === 'public' && mode === 'public' ? resume.token : null}
          lift={!resume && !publicBlocked ? 'bar' : undefined}
        />
      )}

      {resume && startError && <Callout tone="bad" live>{startError}</Callout>}
      </div>

      {!resume && !publicBlocked && (
        <StickyActionBar hint={startError ? <span className="proc-bad-text" role="alert">{startError}</span> : undefined}>
          <Button size="lg" fullWidth loading={starting} leftSection={<IconPlayerPlay size={22} aria-hidden="true" />} onClick={() => void start()}>
            Start
          </Button>
        </StickyActionBar>
      )}
    </div>
  );
}
